import React from 'react';
import {
  Box,
  MenuItem,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Controller } from 'react-hook-form';
import clsx from 'clsx';

const FIELD_BORDER = '#F7F8FA';
const FIELD_BG = '#F7F8FC';
const FIELD_BORDER_EDIT = '#D0D5DD';

const STATUS_COLORS = {
  active: 'rgb(33, 190, 88)',
  blocked: 'rgb(207, 24, 68)',
  // Same red as blocked / license-plate alert (API sets status to "expired")
  expired: 'rgb(207, 24, 68)',
  pending: 'rgb(66, 133, 244)',
  suspended: 'rgb(98, 0, 238)',
  archived: 'rgb(97, 97, 97)',
};

const STATUS_BACKGROUNDS = {
  active: 'rgba(33, 190, 88, 0.08)',
  blocked: 'rgba(207, 24, 68, 0.08)',
  expired: 'rgba(207, 24, 68, 0.08)',
  pending: 'rgba(66, 133, 244, 0.08)',
  suspended: 'rgba(98, 0, 238, 0.08)',
  archived: 'rgba(158, 158, 158, 0.12)',
};

const softOutline = {
  borderColor: `${FIELD_BORDER} !important`,
  borderWidth: '1px !important',
  boxShadow: 'none !important',
};

const editOutline = {
  borderColor: `${FIELD_BORDER_EDIT} !important`,
  borderWidth: '1px !important',
  boxShadow: 'none !important',
};

const useStyles = makeStyles((theme) => ({
  // Rectangular soft box — matches Unit number / Issue date (not a pill chip)
  root: {
    width: 128,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${FIELD_BORDER}`,
    backgroundColor: FIELD_BG,
    backgroundImage: 'none',
    boxSizing: 'border-box',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  // Compact for permit cards
  rootSmall: {
    width: 96,
    height: 28,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  rootEditing: {
    border: `1px solid ${FIELD_BORDER_EDIT}`,
  },
  statusText: {
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1.2,
    textTransform: 'capitalize',
    textAlign: 'center',
    width: '100%',
  },
  statusTextSmall: {
    fontSize: 12,
  },
  selectField: {
    width: 128,
    '& .MuiOutlinedInput-root': {
      height: 36,
      backgroundColor: `${FIELD_BG} !important`,
      backgroundImage: 'none',
      // Kill any residual outer border; outline comes from notchedOutline only
      border: '0 !important',
      boxShadow: 'none !important',
    },
    // Equal L/R padding so label centers in the full box (icon overlays right)
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'transparent !important',
      fontWeight: 700,
      fontSize: 13,
      textTransform: 'capitalize',
      textAlign: 'center',
      paddingTop: 8,
      paddingBottom: 8,
      paddingLeft: '28px !important',
      paddingRight: '28px !important',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent',
    },
    // Theme MuiSelect.outlined draws a dark #0F2854 border on the select —
    // strip it for Unit Status header and permit status (notchedOutline only).
    '& .MuiSelect-outlined, & .MuiSelect-select': {
      border: '0 !important',
      background: 'transparent !important',
      boxShadow: 'none !important',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': softOutline,
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': softOutline,
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': softOutline,
  },
  selectFieldSmall: {
    width: 96,
    '& .MuiOutlinedInput-root': {
      height: 28,
    },
    '& .MuiOutlinedInput-input': {
      fontSize: 12,
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: '20px !important',
      paddingRight: '20px !important',
    },
    '& .MuiSelect-icon': {
      right: 2,
    },
  },
  selectFieldEditing: {
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': editOutline,
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': editOutline,
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': editOutline,
    '&& .MuiSelect-outlined, && .MuiSelect-select': {
      border: '0 !important',
      background: 'transparent !important',
      boxShadow: 'none !important',
    },
  },
  // Match sibling size="small" outlined fields on the permit edit card (~36px)
  selectFieldFullWidth: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: 36,
      backgroundColor: `${FIELD_BG} !important`,
      border: '0 !important',
      boxShadow: 'none !important',
    },
    '& .MuiOutlinedInput-input, & .MuiSelect-outlined, & .MuiSelect-select': {
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 1.43,
      textAlign: 'left',
      textTransform: 'capitalize',
      minHeight: 'unset !important',
      paddingTop: '6px !important',
      paddingBottom: '6px !important',
      paddingLeft: '14px !important',
      paddingRight: '32px !important',
      border: '0 !important',
      background: 'transparent !important',
      boxShadow: 'none !important',
    },
    '& .MuiSelect-icon': {
      right: 7,
      top: 'calc(50% - 12px)',
    },
  },
}));

/**
 * Rectangular status control (Unit header or compact permit cards).
 * Parent toggles `editable` via dedicated pencil / cancel / save icons.
 * Use size="small" on permit cards (view). Use fullWidth in permit edit rows.
 */
const UnitStatusMetaField = ({
  status,
  editable = false,
  control,
  name = 'status',
  options = [],
  id = 'unit_status_select',
  size = 'medium',
  fullWidth = false,
}) => {
  const classes = useStyles();
  const isSmall = size === 'small' && !fullWidth;
  const normalized = String(status || '')
    .toLowerCase()
    .trim();
  const color = STATUS_COLORS[normalized] || STATUS_COLORS.archived;
  const background =
    STATUS_BACKGROUNDS[normalized] || STATUS_BACKGROUNDS.archived;

  if (editable && control) {
    return (
      <Controller
        name={name}
        control={control}
        defaultValue={normalized}
        render={(props) => {
          // Form may still be '' while the parent shows status from React
          // state/ref — empty string must not win over the known status.
          const selectValue = String(props.value || normalized || '')
            .toLowerCase()
            .trim();

          return (
            <TextField
              id={id}
              select
              size="small"
              margin="none"
              fullWidth={fullWidth}
              name={props.name}
              inputRef={props.ref}
              onBlur={props.onBlur}
              className={clsx(
                !fullWidth && classes.selectField,
                isSmall && classes.selectFieldSmall,
                fullWidth && classes.selectFieldFullWidth,
                classes.selectFieldEditing
              )}
              value={selectValue}
              onChange={(event) => props.onChange(event.target.value)}
            >
              {options.map((item) => (
                <MenuItem
                  key={item.value}
                  value={item.value}
                  style={{ textTransform: 'capitalize' }}
                >
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          );
        }}
      />
    );
  }

  return (
    <Box
      id={`unit_status_display_${normalized || 'unknown'}`}
      className={clsx(classes.root, isSmall && classes.rootSmall)}
      style={{ backgroundColor: background }}
    >
      <Typography
        className={clsx(classes.statusText, isSmall && classes.statusTextSmall)}
        style={{ color }}
      >
        {normalized || '—'}
      </Typography>
    </Box>
  );
};

export default UnitStatusMetaField;
